import * as React from "react";
import { MouseEvent, useEffect, useMemo, useRef, useState } from "react";

import { useEditor } from "@grapesjs/react";
import {
  ChevronDown,
  ChevronRight,
  Eye,
  EyeOff,
} from "@hl/base-components/lib/assets/icons/HDS";
import { Box, Center, Group, Stack, Text } from "@mantine/core";
import type { Component } from "grapesjs";

export declare interface LayerItemProps
  extends React.HTMLProps<HTMLDivElement> {
  component: Component;
  level: number;
  draggingCmp?: Component;
  dragParent?: Component;
}

export default function LayerItem({
  component,
  draggingCmp,
  dragParent,
  ...props
}: LayerItemProps) {
  const editor = useEditor();
  const layerRef = useRef<HTMLDivElement>(null);
  const { Layers } = editor;
  const [layerData, setLayerData] = useState(Layers.getLayerData(component));
  const { open, selected, hovered, components, visible, name } = layerData;

  const componentsIds = components.map((cmp) => cmp.getId());
  const isDragging = draggingCmp === component;
  const cmpHash = componentsIds.join("-");
  const level = props.level + 1;
  const isHovered = hovered || dragParent === component;

  useEffect(() => {
    level === 0 && setLayerData(Layers.getLayerData(component));
    if (layerRef.current) {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      (layerRef.current as any).__cmp = component;
    }
  }, [component]);

  useEffect(() => {
    const up = (cmp: Component) => {
      cmp === component && setLayerData(Layers.getLayerData(cmp));
    };
    const ev = Layers.events.component;
    editor.on(ev, up);

    return () => {
      editor.off(ev, up);
    };
  }, [editor, Layers, component]);

  const cmpToRender = useMemo(() => {
    return components.map((cmp) => (
      <LayerItem
        key={cmp.getId()}
        component={cmp}
        level={level}
        draggingCmp={draggingCmp}
        dragParent={dragParent}
      />
    ));
  }, [cmpHash, draggingCmp, dragParent]);

  const toggleOpen = (ev: MouseEvent) => {
    ev.stopPropagation();
    Layers.setLayerData(component, { open: !open });
  };

  const toggleVisibility = (ev: MouseEvent) => {
    ev.stopPropagation();
    Layers.setLayerData(component, { visible: !visible });
  };

  const select = (event: MouseEvent) => {
    event.stopPropagation();
    Layers.setLayerData(component, { selected: true }, { event });
  };

  const hover = (hovered: boolean) => {
    if (!hovered || !draggingCmp) {
      Layers.setLayerData(component, { hovered });
    }
  };

  const hasChildComponents = components.length > 0;

  return (
    <Stack
      className="layer-item"
      sx={{
        // background: selected ? "var(--mantine-color-blue-9)" : "transparent",
        opacity: !visible || isDragging ? 0.5 : 1,
      }}
      spacing={0}
    >
      <Box
        ref={layerRef}
        data-layer-item
        onClick={select}
        onMouseEnter={() => hover(true)}
        onMouseLeave={() => hover(false)}
      >
        <Group
          sx={{
            background: selected
              ? "var(--mantine-color-blue-2)"
              : isHovered
              ? "var(--mantine-color-gray-3)"
              : "transparent",
            opacity: !visible || isDragging ? 0.5 : 1,
            borderRadius: 6,
            cursor: "pointer",
          }}
          noWrap
          align="center"
          spacing={4}
          px={"xs"}
        >
          <Box
            sx={{
              // marginLeft: level * 8,
              flex: 0,
            }}
            py={4}
            pl={level * 8}
            onClick={(event) => {
              if (hasChildComponents) {
                toggleOpen(event);
              }
            }}
          >
            {hasChildComponents ? (
              <Center>
                {open ? (
                  <ChevronDown height={16} />
                ) : (
                  <ChevronRight height={16} />
                )}
              </Center>
            ) : (
              <Center>
                <Box w={24}></Box>
              </Center>
            )}
          </Box>

          <Text
            sx={{
              minWidth: 0,
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
              flexGrow: 1,
              width: "100%",
            }}
            py={8}
          >
            {name}
          </Text>

          <Box
            sx={{
              opacity: hovered
                ? visible
                  ? 1
                  : 0.2
                : visible === false
                ? 0.5
                : 0.0,
              cursor: "pointer",
            }}
            onClick={toggleVisibility}
          >
            <Center>
              {visible ? <Eye height={16} /> : <EyeOff height={16} />}
            </Center>
          </Box>
        </Group>
      </Box>
      {!!(open && components.length) && (
        <Box
          sx={{
            width: "100%",
            display: open ? "block" : "none",
          }}
        >
          {cmpToRender}
        </Box>
      )}
    </Stack>
  );
}
